import apiClient from './apiClient';

/**
 * Cache für Bankkonto-Daten mit Time-to-Live.
 * Reduziert redundante API-Aufrufe bei Navigation und Tab-Wechsel.
 *
 * Pattern analog zu utilityBillCacheService.js, aber mit:
 * - Längerer TTL (15min statt 5min) da Stammdaten
 * - Expliziter Invalidierung nach Mutationen
 * - ETag-Support (304 Not Modified)
 * - localStorage-Persistenz (überlebt Page-Reload)
 * - Request-Deduplizierung (Cache-Stampede-Schutz)
 */
const CACHE_VERSION = 'v1';
const CACHE_PREFIX = `bankAccounts:${CACHE_VERSION}:`;
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 Minuten (Stammdaten)

// In-Memory Cache als Fallback (Private Mode, localStorage nicht verfügbar)
const memoryCache = new Map();
const pendingRequests = new Map();

// Invalidation-Token für Race-Condition-Schutz bei clearCache()
let cacheInvalidationToken = 0;

/**
 * Prüft ob localStorage verfügbar ist.
 * @returns {boolean}
 */
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__ba_storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const storageAvailable = isLocalStorageAvailable();

const isFresh = (entry) => {
  return !!entry && Date.now() - entry.timestamp < CACHE_TTL_MS;
};

/**
 * Liest Cache-Eintrag aus localStorage.
 * @param {string} key - Cache-Key (ohne Prefix)
 * @returns {object|null} Cache-Eintrag oder null wenn nicht vorhanden/ungültig
 */
const getFromLocalStorage = (key) => {
  if (!storageAvailable) return null;
  try {
    const item = localStorage.getItem(CACHE_PREFIX + key);
    if (!item) return null;
    const parsed = JSON.parse(item);
    if (!parsed || typeof parsed.timestamp !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Schreibt Cache-Eintrag in localStorage.
 * @param {string} key - Cache-Key (ohne Prefix)
 * @param {object} value - Cache-Wert { data, timestamp, etag }
 */
const setToLocalStorage = (key, value) => {
  if (!storageAvailable) return;
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota exceeded — Fallback auf Memory-Cache
  }
};

const removeFromLocalStorage = (key) => {
  if (!storageAvailable) return;
  try {
    localStorage.removeItem(CACHE_PREFIX + key);
  } catch {
    // Ignorieren bei Fehlern
  }
};

const invalidateCacheKey = (key) => {
  memoryCache.delete(key);
  pendingRequests.delete(key);
  removeFromLocalStorage(key);
};

const wrapSharedPromiseForCaller = (sharedPromise, signal) => {
  if (!signal) {
    return sharedPromise;
  }

  if (signal.aborted) {
    const abortError = new Error('Request aborted');
    abortError.name = 'AbortError';
    return Promise.reject(abortError);
  }

  return new Promise((resolve, reject) => {
    const handleAbort = () => {
      signal.removeEventListener('abort', handleAbort);
      const abortError = new Error('Request aborted');
      abortError.name = 'AbortError';
      reject(abortError);
    };

    signal.addEventListener('abort', handleAbort, { once: true });

    sharedPromise.then(
      (value) => {
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      }
    );
  });
};

/**
 * Löscht alle Cache-Einträge aus localStorage.
 */
const clearLocalStorageCache = () => {
  if (!storageAvailable) return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignorieren bei Fehlern
  }
};

/**
 * Generische Cache-Funktion für GET-Requests auf BankAccount-Endpoints.
 * Unterstützt localStorage-Persistenz und ETag-basierte Revalidierung.
 * @param {string} url - API-Pfad (z.B. '/bank-accounts/list')
 * @param {object} [params] - Query-Parameter
 * @param {AbortSignal} [signal] - AbortController-Signal
 * @param {number} [_retryCount=0] - Interner Retry-Zähler
 * @returns {Promise<Array|object>} Die Daten (defensive Kopie bei Arrays)
 */
const getCached = (url, params, signal, _retryCount = 0) => {
  const cacheKey = params ? `${url}?${JSON.stringify(params)}` : url;

  // 1. Prüfe Memory-Cache (schnellster Pfad)
  const memoryCached = memoryCache.get(cacheKey);
  if (isFresh(memoryCached)) {
    return Promise.resolve(Array.isArray(memoryCached.data) ? [...memoryCached.data] : memoryCached.data);
  }
  if (memoryCached) {
    memoryCache.delete(cacheKey);
  }

  // 2. Prüfe localStorage-Cache
  const localCached = getFromLocalStorage(cacheKey);
  if (isFresh(localCached)) {
    memoryCache.set(cacheKey, localCached);
    return Promise.resolve(Array.isArray(localCached.data) ? [...localCached.data] : localCached.data);
  }
  if (localCached) {
    removeFromLocalStorage(cacheKey);
  }

  // 3. Deduplizierung: laufenden Request wiederverwenden
  if (pendingRequests.has(cacheKey)) {
    return wrapSharedPromiseForCaller(
      pendingRequests.get(cacheKey).then(
        data => Array.isArray(data) ? [...data] : data
      ),
      signal
    );
  }

  // 4. API-Request mit ETag-Support
  const config = {
    validateStatus: (status) => status === 200 || status === 304
  };
  if (params) config.params = params;

  // ETag senden falls vorhanden (Revalidierung)
  const staleEntry = localCached || memoryCached;
  const etag = staleEntry?.etag;
  if (etag) {
    config.headers = { 'If-None-Match': etag };
  }

  const currentToken = cacheInvalidationToken;

  const promise = apiClient.get(url, config).then(response => {
    // RACE-SCHUTZ: Nicht cachen wenn clearCache()/invalidate aufgerufen wurde
    if (currentToken !== cacheInvalidationToken) {
      pendingRequests.delete(cacheKey);
      if (response.status === 304) {
        const cachedData = staleEntry?.data;
        if (cachedData) return cachedData;
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, _retryCount + 1);
        }
        return Array.isArray(response.data) ? [...response.data] : (response.data || []);
      }
      return Array.isArray(response.data) ? [...response.data] : response.data;
    }

    if (response.status === 304) {
      // 304 Not Modified: Cached Daten nutzen, Timestamp aktualisieren
      if (!staleEntry) {
        pendingRequests.delete(cacheKey);
        invalidateCacheKey(cacheKey);
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, _retryCount + 1);
        }
        return [];
      }

      const refreshedEntry = {
        data: staleEntry.data,
        timestamp: Date.now(),
        etag: staleEntry.etag
      };
      memoryCache.set(cacheKey, refreshedEntry);
      setToLocalStorage(cacheKey, refreshedEntry);
      pendingRequests.delete(cacheKey);
      return staleEntry.data;
    }

    // 200 OK: Neue Daten cachen
    const data = response.data;
    const responseEtag = response.headers['etag'];
    const newEntry = {
      data,
      timestamp: Date.now(),
      etag: responseEtag
    };
    memoryCache.set(cacheKey, newEntry);
    setToLocalStorage(cacheKey, newEntry);
    pendingRequests.delete(cacheKey);
    return data;
  }).catch(err => {
    pendingRequests.delete(cacheKey);
    throw err;
  });

  pendingRequests.set(cacheKey, promise);

  return wrapSharedPromiseForCaller(
    promise.then(data => Array.isArray(data) ? [...data] : data),
    signal
  );
};

const bankAccountCacheService = {
  /**
   * Lädt alle Bankkonten (gecacht).
   * @param {object} [params] - Filter-Parameter
   * @param {AbortSignal} [signal] - AbortController-Signal
   * @returns {Promise<Array>} Liste der BankAccountDTOs
   */
  getBankAccounts: (params, signal) => getCached('/bank-accounts/list', params, signal),

  /**
   * Lädt ein einzelnes Bankkonto (gecacht).
   * @param {string} id - UUID des Bankkontos
   * @param {AbortSignal} [signal] - AbortController-Signal
   * @returns {Promise<object>} BankAccountDTO
   */
  getBankAccount: (id, signal) => getCached(`/bank-accounts/${id}`, undefined, signal),

  /**
   * Invalidiert den List-Cache (nach Create, Update, Delete).
   */
  invalidateList: () => {
    // Token erhöhen, damit in-flight GETs ihre Response nicht mehr in den Cache schreiben
    cacheInvalidationToken++;
    // Alle List-Cache-Einträge löschen (verschiedene Filter-Kombinationen)
    for (const key of [...memoryCache.keys()]) {
      if (key === '/bank-accounts/list' || key.startsWith('/bank-accounts/list?')) {
        memoryCache.delete(key);
        pendingRequests.delete(key);
        removeFromLocalStorage(key);
      }
    }
    // Auch aus localStorage löschen (falls nicht im Memory-Cache)
    if (storageAvailable) {
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const storageKey = localStorage.key(i);
          if (storageKey && storageKey.startsWith(CACHE_PREFIX)) {
            const cacheKey = storageKey.substring(CACHE_PREFIX.length);
            if (cacheKey === '/bank-accounts/list' || cacheKey.startsWith('/bank-accounts/list?')) {
              localStorage.removeItem(storageKey);
            }
          }
        }
      } catch {
        // Ignorieren
      }
    }
  },

  /**
   * Invalidiert den Detail-Cache für ein bestimmtes Bankkonto.
   * @param {string} id - UUID des Bankkontos
   */
  invalidateDetail: (id) => {
    // Token erhöhen, damit in-flight GETs ihre Response nicht mehr in den Cache schreiben
    cacheInvalidationToken++;
    const detailKey = `/bank-accounts/${id}`;
    invalidateCacheKey(detailKey);
  },

  /**
   * Invalidiert alle BankAccount-Caches (List + Detail).
   */
  invalidateAll: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  },

  /**
   * Löscht den kompletten Cache (bei Logout).
   */
  clearCache: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  }
};

export default bankAccountCacheService;
